import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Reveal, useSpotlight } from '@/design-system/motion'
import { Container, Overline, SectionTitle } from '@/design-system/primitives'

import type { RubricFlags } from '../useRubrics'

interface Rubric {
  to: string
  key: string
  emoji: string
  /** Masquée tant qu'il n'y a rien à montrer — une rubrique vide déçoit. */
  available: boolean
}

/**
 * Les rubriques, depuis la page d'accueil.
 *
 * La page d'accueil ne raconte plus toute la soirée : elle donne l'essentiel
 * et mène au billet. Le détail — la carte, le programme, le concours, les
 * résultats — vit dans des pages dédiées, atteignables d'un geste.
 *
 * Empiler onze sections faisait une page qu'on abandonne avant le bouton
 * d'achat. Ces cartes tiennent en un écran.
 */
export function RubricsSection({ flags }: { flags: RubricFlags }) {
  const { t } = useTranslation()

  const rubrics: Rubric[] = [
    { to: '/commander', key: 'onsite', emoji: '🛎️', available: flags.onsite },
    { to: '/carte', key: 'menu', emoji: '🍽️', available: flags.menu },
    { to: '/programme', key: 'program', emoji: '🎭', available: flags.program },
    { to: '/concours', key: 'competition', emoji: '👑', available: flags.competition },
    { to: '/resultats', key: 'results', emoji: '🏆', available: flags.votes },
    { to: '/infos', key: 'info', emoji: 'ℹ️', available: flags.info },
  ].filter((rubric) => rubric.available)

  if (rubrics.length === 0) return null

  return (
    <section
      id="rubriques"
      className="scroll-mt-24 py-[var(--space-section)] sm:py-[var(--space-section-lg)]"
    >
      <Container>
        <Reveal>
          <Overline>{t('rubrics.overline')}</Overline>
          <SectionTitle>{t('rubrics.title')}</SectionTitle>
        </Reveal>

        <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rubrics.map((rubric, index) => (
            // Cascade : chaque carte entre 70 ms après la précédente. Le
            // décalage est plafonné, sinon la dernière carte d'une longue
            // grille se ferait attendre après la fin du défilement.
            <Reveal as="li" key={rubric.key} delay={Math.min(index, 5) * 70}>
              <RubricCard rubric={rubric} />
            </Reveal>
          ))}
        </ul>
      </Container>
    </section>
  )
}

function RubricCard({ rubric }: { rubric: Rubric }) {
  const { t } = useTranslation()
  const ref = useSpotlight<HTMLAnchorElement>()

  return (
    <Link
      ref={ref}
      to={rubric.to}
      className="spotlight edge-glow lift group flex h-full flex-col rounded-lg border border-line-subtle bg-bg-elevated p-6 hover:border-champagne-400/50"
    >
      <span
        className="text-3xl leading-none transition-transform duration-[var(--dur-base)] ease-[var(--ease-spring)] group-hover:-rotate-6 group-hover:scale-110"
        aria-hidden
      >
        {rubric.emoji}
      </span>
      <h3 className="mt-4 font-display text-heading-sm text-content-primary">
        {t(`rubrics.${rubric.key}.title`)}
      </h3>
      <p className="mt-2 text-sm text-content-secondary">{t(`rubrics.${rubric.key}.body`)}</p>
      <span className="mt-4 inline-flex items-center gap-1.5 text-caption text-champagne-300">
        {t('rubrics.open')}
        <span
          aria-hidden
          className="transition-transform duration-[var(--dur-base)] ease-out group-hover:translate-x-1"
        >
          →
        </span>
      </span>
    </Link>
  )
}
